export type NamedVariantPreviewImpactLine = {
  material_code: string;
  material_name: string;
  quantity: number;
  slot_label: string;
  unit_cost: number;
  extended_cost: number;
  is_changed?: boolean;
  replaced_material_code?: string;
  replaced_material_name?: string;
};

export type NamedVariantPreviewImpactSlot = {
  slot_label: string;
  before_qty: number;
  after_qty: number;
  add_qty: number;
  material_codes: string[];
};

export type NamedVariantPreviewImpact = {
  materials_affected: number;
  qty_added: number;
  cost_delta: number;
  slots: NamedVariantPreviewImpactSlot[];
};

/**
 * Summarize before→after impact from a resolved preview BOM.
 * Pure delta adds: unchanged parent lines = before; changed lines append after.
 * Transform swaps (replaced_* set): qty treated as same before/after.
 */
export function summarizeNamedVariantPreviewImpact(
  lines: readonly NamedVariantPreviewImpactLine[],
): NamedVariantPreviewImpact {
  type Acc = {
    before: number;
    after: number;
    changedCodes: Set<string>;
  };
  const bySlot = new Map<string, Acc>();
  let costBefore = 0;
  let costAfter = 0;
  const affected = new Set<string>();

  for (const ln of lines) {
    const qty = Number(ln.quantity);
    const ext = Number(ln.extended_cost);
    const q = Number.isFinite(qty) ? qty : 0;
    const e = Number.isFinite(ext) ? ext : 0;
    costAfter += e;

    const slot = (ln.slot_label || "").trim() || "(no slot)";
    let acc = bySlot.get(slot);
    if (!acc) {
      acc = { before: 0, after: 0, changedCodes: new Set() };
      bySlot.set(slot, acc);
    }
    acc.after += q;

    if (ln.is_changed) {
      const matKey = (ln.material_code || ln.material_name || "?").trim() || "?";
      affected.add(matKey);
      acc.changedCodes.add(matKey);
      const isSwap = Boolean(
        (ln.replaced_material_code || "").trim() || (ln.replaced_material_name || "").trim(),
      );
      if (isSwap) {
        // In-place transform: line still represents the parent qty.
        acc.before += q;
        costBefore += e;
      }
      // Pure add: omitted from before / costBefore.
    } else {
      acc.before += q;
      costBefore += e;
    }
  }

  const slots: NamedVariantPreviewImpactSlot[] = [...bySlot.entries()]
    .filter(([, v]) => v.after !== v.before || v.changedCodes.size > 0)
    .map(([slot_label, v]) => ({
      slot_label,
      before_qty: v.before,
      after_qty: v.after,
      add_qty: v.after - v.before,
      material_codes: [...v.changedCodes],
    }))
    .sort((a, b) => a.slot_label.localeCompare(b.slot_label));

  return {
    materials_affected: affected.size,
    qty_added: slots.reduce((sum, s) => sum + s.add_qty, 0),
    cost_delta: costAfter - costBefore,
    slots,
  };
}

export function formatImpactQty(n: number): string {
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
}
